<script setup>
import { computed, ref } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { content, createdAt, contentAttributes } = useMessageContext();
const { t } = useI18n();
const previewDialogRef = ref(null);

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

const adContext = computed(() => {
  const data = contentAttributes.value?.data || null;
  const eventType = data?.eventType || data?.event_type;

  if (!data || eventType !== 'meta_ad_context') {
    return null;
  }

  return {
    channel: data.channel,
    title: data.title || content.value,
    description: data.description,
    mediaUrl: data.mediaUrl || data.media_url,
    mediaType: data.mediaType || data.media_type,
    photoUrl: data.photoUrl || data.photo_url,
    videoUrl: data.videoUrl || data.video_url,
    thumbnailUrl: data.thumbnailUrl || data.thumbnail_url,
    adId: data.adId || data.ad_id,
    postId: data.postId || data.post_id,
    sourceUrl: data.sourceUrl || data.source_url,
    source: data.source,
    type: data.type,
  };
});

const mediaUrl = computed(() => {
  return (
    adContext.value?.photoUrl ||
    adContext.value?.videoUrl ||
    adContext.value?.mediaUrl ||
    adContext.value?.thumbnailUrl ||
    ''
  );
});

const mediaType = computed(() => {
  return (
    adContext.value?.mediaType ||
    (adContext.value?.videoUrl ? 'video' : 'image')
  );
});

const sourceValueLabel = computed(() => {
  const value = adContext.value?.source;
  const labels = {
    ADS: t('CONVERSATION.AD_CONTEXT.SOURCE_VALUES.ADS'),
    ad: t('CONVERSATION.AD_CONTEXT.SOURCE_VALUES.ad'),
  };

  return labels[value] || value || '';
});

const typeValueLabel = computed(() => {
  const value = adContext.value?.type;
  const labels = {
    OPEN_THREAD: t('CONVERSATION.AD_CONTEXT.TYPE_VALUES.OPEN_THREAD'),
  };

  return labels[value] || value || '';
});

const detailChips = computed(() => {
  return [
    adContext.value?.source
      ? t('CONVERSATION.AD_CONTEXT.SOURCE', {
          value: sourceValueLabel.value,
        })
      : null,
    adContext.value?.type
      ? t('CONVERSATION.AD_CONTEXT.ACTION', {
          value: typeValueLabel.value,
        })
      : null,
    adContext.value?.adId
      ? t('CONVERSATION.AD_CONTEXT.AD_ID', { id: adContext.value.adId })
      : null,
    adContext.value?.postId
      ? t('CONVERSATION.AD_CONTEXT.POST_ID', { id: adContext.value.postId })
      : null,
  ].filter(Boolean);
});

const channelMeta = computed(() => {
  const metaByChannel = {
    messenger: {
      label: t('CONVERSATION.AD_CONTEXT.CHANNELS.MESSENGER'),
      icon: 'i-ri-messenger-fill',
      badgeClass: 'bg-[#eef4ff] text-[#1560ff]',
      accentClass: 'from-[#1560ff]/18 via-[#1560ff]/8 to-transparent',
    },
    instagram: {
      label: t('CONVERSATION.AD_CONTEXT.CHANNELS.INSTAGRAM'),
      icon: 'i-ri-instagram-fill',
      badgeClass: 'bg-[#fff0f6] text-[#d63384]',
      accentClass: 'from-[#d63384]/18 via-[#d63384]/8 to-transparent',
    },
    whatsapp: {
      label: t('CONVERSATION.AD_CONTEXT.CHANNELS.WHATSAPP'),
      icon: 'i-ri-whatsapp-fill',
      badgeClass: 'bg-[#eafaf1] text-[#128c4a]',
      accentClass: 'from-[#128c4a]/18 via-[#128c4a]/8 to-transparent',
    },
  };

  return (
    metaByChannel[adContext.value?.channel] || {
      label: t('CONVERSATION.AD_CONTEXT.CHANNELS.DEFAULT'),
      icon: 'i-ri-megaphone-fill',
      badgeClass: 'bg-n-alpha-2 text-n-slate-12',
      accentClass: 'from-n-alpha-2 via-transparent to-transparent',
    }
  );
});

const hasDescription = computed(() => Boolean(adContext.value?.description));

const openPreview = () => {
  if (!mediaUrl.value) {
    return;
  }

  previewDialogRef.value?.open();
};

const closePreview = () => {
  previewDialogRef.value?.close();
};
</script>

<template>
  <BaseBubble
    v-tooltip.top="readableTime"
    class="!bg-transparent !p-0 !rounded-2xl flex min-w-0 items-center gap-2 shadow-none"
    data-bubble-name="meta-ad-context"
  >
    <div
      class="w-full max-w-[min(36rem,calc(100vw-3rem))] overflow-hidden rounded-[1.75rem] border border-n-weak bg-n-card shadow-sm"
    >
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b"
        :class="channelMeta.accentClass"
      />

      <div
        class="relative flex flex-col gap-3 border-b border-n-weak px-4 py-4 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="flex min-w-0 items-center gap-3">
          <div
            class="flex size-11 shrink-0 items-center justify-center rounded-[1.1rem] ring-1 ring-black/5"
            :class="channelMeta.badgeClass"
          >
            <Icon :icon="channelMeta.icon" class="text-lg" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="inline-flex items-center rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-n-slate-11"
              >
                {{ t('CONVERSATION.AD_CONTEXT.BADGE') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ readableTime }}
              </span>
            </div>
            <p class="mt-2 m-0 text-sm font-semibold leading-5 text-n-slate-12">
              {{ channelMeta.label }}
            </p>
            <p class="m-0 text-sm leading-5 text-n-slate-11">
              {{ t('CONVERSATION.AD_CONTEXT.SUBTITLE') }}
            </p>
          </div>
        </div>

        <div class="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          <a
            v-if="adContext?.sourceUrl"
            :href="adContext.sourceUrl"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-n-brand px-4 text-sm font-medium text-white shadow-sm outline outline-1 outline-transparent transition-all duration-100 ease-out hover:brightness-110 focus-visible:outline-n-brand focus-visible:brightness-110 active:scale-[0.98] sm:min-w-[11rem]"
          >
            <Icon icon="i-lucide-external-link" class="size-4 shrink-0" />
            {{ t('CONVERSATION.AD_CONTEXT.OPEN_LINK') }}
          </a>
        </div>
      </div>

      <div
        v-if="mediaUrl"
        class="relative h-44 w-full overflow-hidden bg-n-slate-3 sm:h-48"
      >
        <button
          type="button"
          class="block h-full w-full cursor-zoom-in overflow-hidden text-left"
          :aria-label="t('CONVERSATION.AD_CONTEXT.OPEN_PREVIEW')"
          @click="openPreview"
        >
          <img
            v-if="mediaType === 'image'"
            :src="mediaUrl"
            :alt="adContext?.title || 'Meta ad context'"
            class="block h-full w-full object-cover transition-transform duration-200 hover:scale-[1.02]"
          />
          <div v-else class="relative h-full w-full overflow-hidden">
            <video
              :src="mediaUrl"
              muted
              playsinline
              preload="metadata"
              class="block h-full w-full bg-black object-cover"
            />
            <div
              class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20"
            >
              <span
                class="inline-flex size-12 items-center justify-center rounded-full bg-black/70 text-white shadow-lg backdrop-blur-sm"
              >
                <Icon icon="i-lucide-play" class="text-lg ltr:ml-0.5" />
              </span>
            </div>
          </div>
        </button>
      </div>

      <div
        v-else
        class="flex aspect-[16/9] items-end bg-gradient-to-br from-n-slate-3 via-n-alpha-2 to-n-slate-2 px-4 py-4"
      >
        <div class="rounded-2xl bg-n-alpha-black2 px-3 py-2 backdrop-blur-sm">
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.AD_CONTEXT.NO_MEDIA') }}
          </p>
          <p class="m-0 mt-1 text-xs text-n-slate-11">
            {{ channelMeta.label }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-3 px-4 py-4">
        <div class="space-y-2">
          <p
            v-if="adContext?.title"
            class="m-0 text-[1rem] font-semibold leading-6 text-n-slate-12 sm:text-[1.075rem]"
          >
            {{ adContext.title }}
          </p>
          <p
            v-if="hasDescription"
            class="m-0 text-sm leading-6 text-n-slate-11 sm:text-[0.95rem]"
          >
            {{ adContext.description }}
          </p>
        </div>

        <div v-if="detailChips.length" class="flex flex-wrap gap-2">
          <span
            v-for="chip in detailChips"
            :key="chip"
            class="inline-flex items-center rounded-full border border-n-weak bg-n-surface-2 px-3 py-1.5 text-xs font-medium text-n-slate-11"
          >
            {{ chip }}
          </span>
        </div>
      </div>
    </div>
  </BaseBubble>

  <Dialog
    ref="previewDialogRef"
    :title="adContext?.title || t('CONVERSATION.AD_CONTEXT.PREVIEW_TITLE')"
    width="3xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="closePreview"
  >
    <template #default>
      <div class="flex flex-col gap-4">
        <div class="relative overflow-hidden rounded-[1.5rem] bg-black">
          <button
            type="button"
            class="absolute right-3 top-3 z-10 inline-flex size-10 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm transition hover:bg-black"
            :aria-label="t('CONVERSATION.AD_CONTEXT.CLOSE_PREVIEW')"
            @click="closePreview"
          >
            <Icon icon="i-lucide-x" class="text-lg" />
          </button>

          <img
            v-if="mediaType === 'image'"
            :src="mediaUrl"
            :alt="adContext?.title || 'Meta ad context'"
            class="block max-h-[80vh] w-full object-contain"
          />
          <video
            v-else
            :src="mediaUrl"
            controls
            autoplay
            preload="metadata"
            class="block max-h-[80vh] w-full bg-black object-contain"
          />
        </div>

        <div v-if="hasDescription" class="px-1">
          <p class="m-0 text-sm leading-6 text-n-slate-11">
            {{ adContext.description }}
          </p>
        </div>
      </div>
    </template>
  </Dialog>
</template>
